import { fromJS } from 'immutable';
import moment from 'moment';
import { normalize } from 'normalizr';

import { mapListToRecords, normalizeList, websocketsHandlerFactory } from '@sophocles/entities/entityUtils';
import {
  RelayLoadContract,
  relayLoadContractSchema,
} from '@sophocles/entities/RelayLoadContract/relayLoadContractRecord';
import { addToast } from '@sophocles/features/toast';
import apiClient from '@sophocles/http-common';
import { checkHttpStatus, genericErrorAction } from '@sophocles/utils';

/* Receive Actions */
export const RECEIVE_RELAY_LOAD_CONTRACTS = 'RECEIVE_RELAY_LOAD_CONTRACTS';
function receiveRelayLoadContracts(json) {
  return {
    type: RECEIVE_RELAY_LOAD_CONTRACTS,
    relayLoadContracts: mapListToRecords(json, RelayLoadContract),
  };
}

/* Request Actions */
// starter request
export const REQUEST_RELAY_LOAD_CONTRACT = 'REQUEST_RELAY_LOAD_CONTRACT';
function requestRelayLoadContract(relayLoadContract_id) {
  return {
    type: REQUEST_RELAY_LOAD_CONTRACT,
    relayLoadContract_id,
  };
}

export const REQUEST_RELAY_LOAD_CONTRACT_FOR_SITE = 'REQUEST_RELAY_LOAD_CONTRACT_FOR_SITE';
function requestRelayLoadContractForSite(site_id) {
  return {
    type: REQUEST_RELAY_LOAD_CONTRACT_FOR_SITE,
    site_id,
  };
}

export const REQUEST_RELAY_LOAD_CONTRACT_FOR_RELAY_LOAD = 'REQUEST_RELAY_LOAD_CONTRACT_FOR_RELAY_LOAD';
function requestRelayLoadContractForRelayLoad(load_id) {
  return {
    type: REQUEST_RELAY_LOAD_CONTRACT_FOR_RELAY_LOAD,
    load_id,
  };
}

/* Fetch Actions */
// starter fetch

export function fetchAllRelayLoadContractsForRelayLoad(relayLoad) {
  return dispatch => {
    dispatch(requestRelayLoadContract(relayLoad));
    return apiClient
      .get(`/relayloadcontracts/?relay_load=${relayLoad}`)
      .then(checkHttpStatus)
      .then(response => fromJS(response.data))
      .catch(error => dispatch(genericErrorAction(error, RECEIVE_RELAY_LOAD_CONTRACTS)));
  };
}

export function fetchRelayLoadContract(relayLoadContract_id) {
  return dispatch => {
    dispatch(requestRelayLoadContract(relayLoadContract_id));
    return apiClient
      .get(`/relayloadcontracts/${relayLoadContract_id}/`)
      .then(checkHttpStatus)
      .then(response => normalizeList(response.data, 'relayLoadContracts', relayLoadContractSchema))
      .then(json => dispatch(receiveRelayLoadContracts(json.entities.relayLoadContracts)))
      .catch(error => {
        dispatch(genericErrorAction(error, REQUEST_RELAY_LOAD_CONTRACT));
      });
  };
}

export function fetchRelayLoadContractForSite(site_id) {
  const today = moment().format('yyyy-MM-dd');
  return dispatch => {
    dispatch(requestRelayLoadContractForSite(site_id));
    return apiClient
      .get('/relayloadcontracts/', {
        params: {
          relay_load__primary_relay__relay_controller__site: site_id,
          contract_start__lte: today,
          contract_end__gte: today,
        },
      })
      .then(checkHttpStatus)
      .then(response => normalizeList(response.data.results, 'relayLoadContracts', relayLoadContractSchema))
      .then(json => dispatch(receiveRelayLoadContracts(json.entities.relayLoadContracts)))
      .catch(error => {
        dispatch(genericErrorAction(error, REQUEST_RELAY_LOAD_CONTRACT));
      });
  };
}

export function fetchRelayLoadContractForRelayController(relayController_id) {
  return dispatch =>
    apiClient
      .get(`/relaycontrollers/${relayController_id}/recentcontracts/`)
      .then(checkHttpStatus)
      .then(response => normalizeList(response.data, 'relayLoadContracts', relayLoadContractSchema))
      .then(json => dispatch(receiveRelayLoadContracts(json.entities.relayLoadContracts)))
      .catch(error => {
        dispatch(genericErrorAction(error, REQUEST_RELAY_LOAD_CONTRACT));
      });
}

export function fetchRelayLoadContractForRelayLoad(load_id) {
  return dispatch => {
    dispatch(requestRelayLoadContractForRelayLoad(load_id));
    return apiClient
      .get('/relayloadcontracts/', { params: { relay_load: load_id } })
      .then(checkHttpStatus)
      .then(response => normalizeList(response.data.results, 'relayLoadContracts', relayLoadContractSchema))
      .then(json => dispatch(receiveRelayLoadContracts(json.entities.relayLoadContracts)))
      .catch(error => {
        dispatch(genericErrorAction(error, REQUEST_RELAY_LOAD_CONTRACT));
      });
  };
}

/* Edit Actions */
export const EDIT_RELAY_LOAD_CONTRACT = 'EDIT_RELAY_LOAD_CONTRACT';
export function editRelayLoadContract(relayLoadContract_id) {
  return {
    type: EDIT_RELAY_LOAD_CONTRACT,
    relayLoadContract: relayLoadContract_id,
  };
}

export function patchRelayLoadContract(relayLoadContract_id, values) {
  return dispatch => {
    dispatch(editRelayLoadContract(relayLoadContract_id));
    return apiClient
      .patch(`/relayloadcontracts/${relayLoadContract_id}/`, values)
      .then(checkHttpStatus)
      .then(response => normalize(response.data, relayLoadContractSchema))
      .then(json => {
        dispatch(receiveRelayLoadContracts(json.entities.relayLoadContracts));
      })
      .then(res => {
        dispatch(
          addToast({
            title: 'Success',
            message: 'Contract Updated.',
            level: 'success',
            dismissible: true,
          }),
        );
        return fromJS(res);
      })
      .catch(error => dispatch(genericErrorAction(error, EDIT_RELAY_LOAD_CONTRACT)));
  };
}

/* Create Actions */
export const CREATE_RELAY_LOAD_CONTRACT = 'CREATE_RELAY_LOAD_CONTRACT';
function sayCreateRelayLoadContract(values) {
  return {
    type: CREATE_RELAY_LOAD_CONTRACT,
    values: values,
  };
}

export function createRelayLoadContract(values) {
  return dispatch => {
    dispatch(sayCreateRelayLoadContract(values));
    return apiClient
      .post(`/relayloadcontracts/`, values)
      .then(checkHttpStatus)
      .then(response => normalize(response.data, relayLoadContractSchema))
      .then(json => {
        dispatch(receiveRelayLoadContracts(json.entities.relayLoadContracts));
      })
      .then(res => {
        dispatch(
          addToast({
            title: 'Success',
            message: 'Contract Created.',
            level: 'success',
            dismissible: true,
          }),
        );
        return fromJS(res);
      })
      .catch(error => dispatch(genericErrorAction(error, CREATE_RELAY_LOAD_CONTRACT)));
  };
}

/* Delete Actions */
export const DELETE_RELAY_LOAD_CONTRACT = 'DELETE_RELAY_LOAD_CONTRACT';
function sayDeleteRelayLoadContract(relayLoadContract_id) {
  return {
    type: DELETE_RELAY_LOAD_CONTRACT,
    relayLoadContract: relayLoadContract_id,
  };
}

export function deleteRelayLoadContract(relayLoadContract_id) {
  return dispatch =>
    apiClient
      .delete(`/relayloadcontracts/${relayLoadContract_id}/`)
      .then(checkHttpStatus)
      .then(r => dispatch(sayDeleteRelayLoadContract(relayLoadContract_id)))
      .then(res => {
        dispatch(
          addToast({
            title: 'Success',
            message: 'Contract Deleted.',
            level: 'success',
            dismissible: true,
          }),
        );
        return fromJS(res);
      })
      .catch(error => dispatch(genericErrorAction(error, DELETE_RELAY_LOAD_CONTRACT)));
}

const receiver = (dispatch, json) => dispatch(receiveRelayLoadContracts(json.entities.relayLoadContracts));

export const handleRelayLoadContractSocketActions = websocketsHandlerFactory(
  receiver,
  sayDeleteRelayLoadContract,
  relayLoadContractSchema,
);
